import { Controller } from "@hotwired/stimulus"
import { describeInputBy } from "../lib/described_by"

// Scopes the agency autocomplete to the agencies belonging to the selected IMO.
// Works with the autocomplete-input dropdowns (not native datalists): it reads
// the IMO option list from the IMO wrapper's data-options JSON, resolves the
// typed/selected IMO name to its organization id, and tells the agency
// autocomplete-input controller to filter its options by that parent id.
//
// When the IMO changes (or is cleared, or is a not-yet-created name), any stale
// agency selection is cleared so the agent can only keep an agency that belongs
// to the current IMO. A typed-but-unknown IMO resolves to a sentinel that
// matches no existing agency, so the agent simply types a new agency name.
export default class extends Controller {
  // agencyLockHint: the visible "Select an IMO first…" line the locked agency
  // field names via aria-describedby (A11Y-1, #13424); lockAgency mirrors it.
  static targets = ["imoInput", "agencyInput", "agencyLockHint"]

  static UNKNOWN_IMO = "__unknown_imo__"

  // Minimum IMO name length before the agency unlocks (matches the server-side
  // Organizations::ImoAgencyResolver minimum).
  static MIN_IMO_LENGTH = 3

  // Junk "no value" names agents type instead of a real org. Matched against the
  // value with case + punctuation stripped, so "N/A", "n/a", "N.A." all hit.
  // "Self" is intentionally absent — it's a valid answer for a solo producer.
  // We don't hard-block these; we clear the field and refocus so the agent
  // re-enters a real name.
  //
  // Fallback used only when the host element carries no `data-junk-names`
  // attribute (old/unwired views). Wired views render this list from the
  // server's Organizations::JunkNames::BLOCKED (single source of truth) —
  // see the `junkNames` getter below. Never hand-type the list in two places.
  static DEFAULT_PLACEHOLDER_NAMES = ["na", "none", "notapplicable", "tbd", "unknown", "nil", "null"]

  connect() {
    this._debounceTimer = null
    this._initialSyncTimer = null
    this._lastImoId = undefined
    // Defer the initial scope so the agency's autocomplete-input controller
    // (a child element) has connected and can receive setParent. Store the
    // handle so a disconnect (e.g. Turbo navigation) before it fires cancels
    // it instead of running syncAgencies on a torn-down controller.
    this._initialSyncTimer = setTimeout(() => this.syncAgencies(), 0)
  }

  disconnect() {
    clearTimeout(this._debounceTimer)
    clearTimeout(this._initialSyncTimer)
  }

  // Reads the junk-name list from `data-junk-names` (JSON, rendered off
  // Organizations::JunkNames::BLOCKED by wired views) and falls back to the
  // hardcoded list for old/unwired views. Never hand-type this list a second
  // time in JS — the server constant is the single source of truth.
  get junkNames() {
    const raw = this.element.dataset.junkNames
    if (!raw) return this.constructor.DEFAULT_PLACEHOLDER_NAMES

    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : this.constructor.DEFAULT_PLACEHOLDER_NAMES
    } catch {
      return this.constructor.DEFAULT_PLACEHOLDER_NAMES
    }
  }

  // Bound to the IMO input's `input` action. Debounced for typing; selection
  // from the dropdown also dispatches `input`, so this covers both paths.
  filterAgencies() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this.syncAgencies(), 150)
  }

  // Bound to the IMO input's `change` action — fires when an IMO is committed
  // (selected from the dropdown, or typed/created then blurred), NOT on every
  // keystroke. Once an IMO exists, move focus to the agency field to guide the
  // flow. syncAgencies() (called below) unlocks the agency first; the focus is
  // deferred so any other listeners on the same bubbled `change` settle first.
  imoCommitted() {
    // A junk IMO ("N/A") is cleared and refocused — don't advance to the agency.
    if (this.rejectPlaceholder(this.imoInputTarget)) return

    this.syncAgencies()
    if (this.resolveImoId() === "" || !this.hasAgencyInputTarget) return

    setTimeout(() => {
      if (!this.agencyInputTarget.disabled) this.agencyInputTarget.focus()
    }, 0)
  }

  // Bound to the agency input's `change` — clear a junk agency name and refocus
  // so the agent re-enters a real one (same rule as the IMO field).
  agencyCommitted() {
    this.rejectPlaceholder(this.agencyInputTarget)
  }

  // If the input holds a junk placeholder, clear it, notify listeners (so the
  // autocomplete + agency lock re-sync), refocus it, and return true so the
  // caller stops. Returns false for a real (or empty) value. Focus is deferred
  // so it survives the in-progress blur that fired this `change`.
  rejectPlaceholder(input) {
    if (!input) return false

    const normalized = input.value.trim().toLowerCase().replace(/[^a-z0-9]/g, "")
    if (normalized === "" || !this.junkNames.includes(normalized)) return false

    input.value = ""
    input.dispatchEvent(new Event("input", { bubbles: true }))
    setTimeout(() => input.focus(), 0)
    return true
  }

  // Handle "org-suggestion:fix-misclassification" (Phase 3d) AND
  // "org-confirm-gate:apply-agency" (Phase 4, the forced-choice gate's "Join
  // {agency} under {imo}" option) — both carry the same { imoName, agencyName }
  // detail shape, so this one handler serves either source. The agent typed a
  // known agency name into the IMO field and picked one of its real parent IMOs.
  // Set IMO to the parent — which unlocks and re-scopes the agency field via
  // syncAgencies — then set the agency to the typed name. Unlike the login-time
  // confirmation modal, this does NOT submit: these are forms with their own
  // Save button, so the agent reviews the correction first.
  applyMisclassification(event) {
    const { imoName, agencyName } = event.detail || {}
    if (!imoName || !agencyName || !this.hasImoInputTarget) return

    this.setFieldValue(this.imoInputTarget, imoName)
    // Defense-in-depth: on both current host views the IMO input wires
    // change->imo-agency#imoCommitted, so the change dispatched above already
    // ran syncAgencies() (unlock + re-scope). We call it again explicitly so
    // applyMisclassification stays correct on any future host that does NOT
    // wire imoCommitted — it must not depend on view-level action wiring to
    // unlock the agency before we write its value. Idempotent when already run.
    this.syncAgencies()
    if (this.hasAgencyInputTarget) this.setFieldValue(this.agencyInputTarget, agencyName)
  }

  // Set a field's value and fire input + change so every peer controller on the
  // field (autocomplete-input, unsaved-changes, this one) re-syncs.
  setFieldValue(input, value) {
    input.value = value
    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  syncAgencies() {
    const imoId = this.resolveImoId()

    // IMO changed → drop a now-invalid agency selection.
    if (this._lastImoId !== undefined && imoId !== this._lastImoId && this.hasAgencyInputTarget) {
      this.agencyInputTarget.value = ""
      this.agencyInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }
    this._lastImoId = imoId

    // Agency belongs to an IMO: lock it until a usable IMO name is present.
    this.lockAgency(!this.imoFilled())

    const agencyController = this.agencyController()
    if (agencyController) agencyController.setParent(imoId)

    this.syncAgencySuggesterParent(imoId)
  }

  // Scope the agency field's fuzzy "did you mean?" suggester (org-suggestion)
  // AND its forced-choice confirmation gate (org-confirm-gate) to the resolved
  // parent IMO, so both only surface/query agencies under the selected IMO. A
  // real org id scopes it; an empty/unknown IMO clears the scope.
  syncAgencySuggesterParent(imoId) {
    if (!this.hasAgencyInputTarget) return

    const wrapper = this.agencyInputTarget.closest(".gl-autocomplete-wrapper")
    if (!wrapper) return

    const parentId = imoId && imoId !== this.constructor.UNKNOWN_IMO ? imoId : ""
    wrapper.dataset.orgSuggestionParentIdValue = parentId
    wrapper.dataset.orgConfirmGateParentIdValue = parentId
  }

  // True once the IMO input holds a usable name (>= MIN_IMO_LENGTH chars).
  imoFilled() {
    if (!this.hasImoInputTarget) return false
    return this.imoInputTarget.value.trim().length >= this.constructor.MIN_IMO_LENGTH
  }

  // Lock/unlock the agency input + placeholder; clearing its value when locked
  // so a re-locked agency can't submit a stale name.
  lockAgency(locked) {
    if (!this.hasAgencyInputTarget) return
    const agency = this.agencyInputTarget
    agency.disabled = locked
    agency.classList.toggle("gl-input-disabled", locked)
    agency.placeholder = locked ? "Select an IMO first..." : "Start typing your agency name..."
    this.syncLockReason(agency, locked)
    if (locked && agency.value) {
      agency.value = ""
      agency.dispatchEvent(new Event("input", { bubbles: true }))
    }
  }

  // A11Y-1: a locked agency field explains why. An <input> cannot host the
  // gl-tooltip pseudo-bubble (lint_icon_button_tooltips rule 5), so the reason
  // is the visible lock hint, shown and named as the field's description while
  // locked; unlocking hides it and withdraws the description, so an enabled
  // field never keeps telling the agent to select an IMO they already chose.
  // The view paints the same pair for the first frame (_business_info).
  //
  // Only the hint's OWN id is added or removed (lib/described_by): the same
  // input is also described by org_confirm_gate_controller's lookup-error
  // alert, and syncAgencies re-locks/unlocks on every IMO keystroke, so a
  // same-state call must never clear a neighbour's still-visible description.
  syncLockReason(agency, locked) {
    if (!this.hasAgencyLockHintTarget) return
    const hint = this.agencyLockHintTarget
    hint.hidden = !locked
    describeInputBy(agency, hint.id, locked)
  }

  // Resolve the IMO input value to an organization id using the IMO option
  // list. Empty input → "" (no scope). A name not in the list → sentinel.
  resolveImoId() {
    const name = this.hasImoInputTarget ? this.imoInputTarget.value.trim().toLowerCase() : ""
    if (!name) return ""

    const match = this.imoOptions().find(o => String(o.name).toLowerCase() === name)
    return match ? String(match.id) : this.constructor.UNKNOWN_IMO
  }

  imoOptions() {
    const wrapper = this.hasImoInputTarget ? this.imoInputTarget.closest(".gl-autocomplete-wrapper") : null
    if (!wrapper?.dataset.options) return []
    try {
      return JSON.parse(wrapper.dataset.options)
    } catch {
      return []
    }
  }

  agencyController() {
    const wrapper = this.hasAgencyInputTarget ? this.agencyInputTarget.closest(".gl-autocomplete-wrapper") : null
    if (!wrapper) return null
    return this.application.getControllerForElementAndIdentifier(wrapper, "autocomplete-input")
  }
}
